import { Edit, Save, Close } from '@mui/icons-material';
import {
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  SxProps,
  Box,
} from '@mui/material';
import * as React from 'react';
import { GENERAL_FONT } from '../../constants/theme';

export interface DropdownWithButtonProps {
  onSave: (newValue: string) => void;
  onEditStart?: () => void;
  label: string;
  value: string;
  currentlyEdited?: boolean;
  noEdit?: boolean;
  options: { value: string; label: string }[];
}

const disabledFieldColorSx: SxProps = {
  '& .MuiInputBase-input': {
    WebkitTextFillColor: 'black !important',
    fontFamily: GENERAL_FONT,
  },
  '& .MuiOutlinedInput-input': {
    WebkitTextFillColor: 'black !important',
    fontFamily: GENERAL_FONT,
  },
  '& .Mui-disabled': {
    WebkitTextFillColor: 'black !important',
    fontFamily: GENERAL_FONT,
  },
};

const selectOverrideSx: SxProps = {
  '& .MuiSelect-select': {
    padding: '16.5px 14px',
  },
};

const inputLabelSx: SxProps = {
  fontFamily: GENERAL_FONT,
  fontSize: '0.875rem',
  transform: 'translate(14px, -6px) scale(0.75)',
  backgroundColor: 'white',
  padding: '0 4px',
};

const DropdownWithButton = ({
  label,
  value,
  currentlyEdited,
  onSave,
  onEditStart,
  options,
  noEdit,
}: DropdownWithButtonProps) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [currentValue, setCurrentValue] = React.useState(value);

  const handleEditClick = () => {
    setIsEditing(true);
    onEditStart?.();
  };
  const handleSaveClick = () => {
    onSave(currentValue);
    setIsEditing(false);
  };
  const handleCloseClick = () => {
    setCurrentValue(value);
    setIsEditing(false);
  };

  const handleChange = (event: SelectChangeEvent<string>) => {
    setCurrentValue(event.target.value);
  };
  React.useEffect(() => {
    if (!currentlyEdited) {
      handleCloseClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resets only when editing ends, so a value change outside editing shows after the next edit
  }, [currentlyEdited]);

  return (
    <FormControl sx={{ m: 1, width: '100%' }} variant="outlined">
      <InputLabel sx={inputLabelSx}>{label}</InputLabel>
      {isEditing ? (
        <Box display="flex" alignItems="center">
          <Select
            value={currentValue}
            onChange={handleChange}
            sx={selectOverrideSx}
            fullWidth
            variant="outlined"
            disableUnderline
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          <IconButton edge="end" aria-label="Close" onClick={handleCloseClick}>
            <Close />
          </IconButton>
          <IconButton aria-label="Save" onClick={handleSaveClick}>
            <Save />
          </IconButton>
        </Box>
      ) : (
        <OutlinedInput
          value={
            options.find((option) => option.value === currentValue)?.label || ''
          }
          notched
          disabled
          sx={disabledFieldColorSx}
          endAdornment={
            !noEdit && (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  aria-label="Edit"
                  onClick={handleEditClick}
                >
                  <Edit />
                </IconButton>
              </InputAdornment>
            )
          }
          label={label}
        />
      )}
    </FormControl>
  );
};

export default DropdownWithButton;
